import * as Yup from 'yup';
import FormGroupComponent, { InputType } from '@spa/components/form-group/form-group.component';
import FormikWithHoney, {
  WithHoneypot
} from '@spa/components/forms/formik-with-honey/formikWithHoney.component';
import HeadlineComponent, {
  Props as HeadlineProps
} from '@spa/components/headline/headline.component';
import React, { Fragment, ReactElement, memo, useState } from 'react';
import ButtonComponent from '../button/button/button.component';
import { ButtonType } from '@spa/components/button/types/ButtonType';
import { CmsComponentContext } from '../container/CmsComponentContext';
import ConfigurableCmsValidity from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-validity.component';
import { FormikHelpers } from 'formik';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { NewsletterOrigin } from '@spa/generated/graphql/graphql';
import NewsletterRegistrationAlert from './newsletter-registration-alert.component';
import { NewsletterRegistrationFormStatus } from '@spa/lib/newsletter/newsletterRegistrationStatus';
import { Store } from 'redux';
import { createEmailSchema } from '@spa/lib/formsSchemas/commonValidations';
import { setUserEmail } from '@spa/lib/store/user/userSlice';
import styles from './newsletter.component.module.scss';
import useHoneypotToken from '../forms/formik-with-honey/useHoneypotToken';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useNewsletterRegistration from '@spa/lib/newsletter/useNewsletterRegistration';
import useNewsletterTracking from '@spa/lib/newsletter/useNewsletterTracking';
import { useStore } from 'react-redux';

export interface Props {
  readonly optionalHeadline: HeadlineProps | null;
  readonly editorMode: boolean;
  readonly dateFrom: number | null;
  readonly dateTo: number | null;
}

interface FormValues {
  readonly email: string;
}

function NewsletterComponent({
  optionalHeadline,
  editorMode,
  dateTo,
  dateFrom
}: Props): ReactElement {
  const { formatMessage }: NamespacedIntl = useNamespacedIntl('newsletter');
  const { formatMessage: formsFormatMessage }: NamespacedIntl = useNamespacedIntl('forms');
  const store: Store = useStore();
  const [showConsent, setShowConsent] = useState(false);

  const honeypotToken: string = useHoneypotToken();
  const { trackNewsletterRegistration } = useNewsletterTracking();
  const { formStatus, handleSubmit, resetSubmissionStatus, submissionError } =
    useNewsletterRegistration<FormValues>({
      consent: formatMessage('widget.consentText'),
      origin: NewsletterOrigin.QUICK_SIGNUP,
      honeypotToken,
      onRegistrationResult: (email: string): void => {
        store.dispatch(setUserEmail({ email }));
      },
      trackNewsletterRegistration
    });

  const isSubmitted = formStatus === NewsletterRegistrationFormStatus.SUBMITTED;

  const initialValues: FormValues = {
    email: ''
  };

  const emailValidationSchema: Yup.Schema<FormValues> = Yup.object().shape({
    email: createEmailSchema(formsFormatMessage)
  });

  const handleEmailChange = (): void => {
    setShowConsent(true);

    if (isSubmitted) {
      resetSubmissionStatus();
    }
  };

  const successMessage = (): ReactElement => {
    return (
      <div className={styles['newsletter__successMessage']}>
        <strong>{formatMessage('widget.registerSuccess.headline')}</strong>
        <span>{formatMessage('widget.registerSuccess.text')}</span>
      </div>
    );
  };

  return (
    <div className={styles.newsletter}>
      {editorMode && <ConfigurableCmsValidity dateFrom={dateFrom} dateTo={dateTo} />}
      <div className={styles['newsletter__text-wrapper']}>
        {optionalHeadline?.headline && (
          <HeadlineComponent
            id={optionalHeadline.id}
            usedInFullWidthComponent={true}
            headline={optionalHeadline.headline}
            subHeadline={optionalHeadline.subHeadline}
            link={optionalHeadline.link}
            context={CmsComponentContext.FOOTER}
          />
        )}
      </div>
      <FormikWithHoney<FormValues>
        formClass={styles['newsletter__form']}
        initialValues={initialValues}
        validationSchema={emailValidationSchema}
        honeypotToken={honeypotToken}
        onSubmit={async (
          values: WithHoneypot<FormValues>,
          { setSubmitting }: FormikHelpers<FormValues>
        ): Promise<void> => {
          await handleSubmit(values);
          setSubmitting(false);
        }}
      >
        {({ errors, touched, values, isSubmitting, isValid, dirty }) => {
          return (
            <FormGroupComponent
              id={optionalHeadline?.id ?? 'newsletter'}
              label={formatMessage('placeholder')}
              name="email"
              touched={touched.email}
              error={errors.email}
              value={values.email}
              type={InputType.EMAIL}
              autoComplete="email"
              isDisabled={isSubmitting}
              onChangeCallback={handleEmailChange}
              onFocusCallback={() => setShowConsent(true)}
              inputButton={
                <ButtonComponent
                  buttonType={ButtonType.SECONDARY}
                  disabled={isSubmitting || isSubmitted}
                  looksDisabled={!isValid || !dirty}
                  text={formatMessage('buttonText')}
                  type="submit"
                />
              }
              required
            />
          );
        }}
      </FormikWithHoney>
      {isSubmitted ? (
        <NewsletterRegistrationAlert
          formatMessage={formatMessage}
          submissionError={submissionError}
          successResponseOverride={successMessage()}
        />
      ) : (
        <Fragment>
          {showConsent && (
            <span
              className={styles['newsletter__consent-text']}
              dangerouslySetInnerHTML={{
                __html: formatMessage('widget.consentText')
              }}
            ></span>
          )}
        </Fragment>
      )}
    </div>
  );
}

export default memo(NewsletterComponent);
